import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Icon } from '../ui/Icon'
import { Sheet } from '../ui/Sheet'
import { ApiError } from '../transport/http'
import {
  clearAgentQueue,
  discardQueuedMessages,
  getAgentQueues,
  releaseAgentQueue,
  releaseQueuedMessages,
} from '../transport/api'
import { QueueGroup, useQueueReasonsLive, useQueueSignal } from './QueueGroup'

/**
 * How much mail the system is sitting on — in the composer, beside Attach files.
 *
 *   owner, 2026-08-07: Terminal / clean view 右上角需要一個 message queue event 的通知
 *   按鈕, 讓使用者可以擴展, 知道有多少信息 queue 在系統裡面, 可以手動放行或是清除.
 *
 * A held message had no presence anywhere you would be looking. `Activity` has a queue
 * tab, but you only open Activity if you already suspect something — and the thing this
 * signals is precisely the thing nobody suspects, because the sender was told `queued`
 * and believed it. Measured: a reply sat on disk for ten minutes with nothing anywhere
 * saying so.
 *
 * IT LIVES IN THE COMPOSER, beside Attach files, and is HIDDEN AT ZERO — owner,
 * 2026-08-07: 挪動到 Text input 這裏, 放在 attach file 按鈕的右邊. (沒有 message count
 * 的時候就隱藏)
 *
 * I had it in the top bar, always rendered, on the reasoning that a control which
 * appears on a non-zero count cannot be found at zero. That reasoning assumed the
 * button was also the way to ASK "is anything stuck?" — but the composer row is where
 * you already are whenever you write to an agent, so appearing there is enough to be
 * seen, which was never true of a chip parked in a corner. Zero has nothing to say, and
 * a permanent control saying it is noise on the row you use most.
 *
 * The full inspection lives in Activity's queue tab, which is reachable at zero and
 * always was, so nothing became unaskable.
 *
 * PER MESSAGE, PER SENDER, PER RECIPIENT — owner, 2026-09-05: 現在只有一些按鈕可以選擇我
 * 要將一堆訊息 deliver 或者是 discard … 針對不同的 Agent 以及不同 Agent 之間傳遞訊息的
 * 每一個欄位做出可以選擇要分別單獨 deliver 或是 discard. Three grains now, smallest
 * first: every row has its own Deliver / Discard; every sender→recipient sub-group has
 * a pair for its messages; the recipient keeps the pair it had. Tick boxes collect a
 * selection across all of it, and the top bar acts on the selection while one exists —
 * "Deliver now" / "Discard all" are what it says when nothing is ticked.
 */
export function QueueBell() {
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set())
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['agent-queues'], queryFn: getAgentQueues })
  // The bell APPEARS on the first hold, so its latency is exactly the latency of
  // this signal — the edge's queue_changed event, not a focus you happen to give it.
  useQueueSignal()
  // The open sheet says why each message waits; keep that true while it is open.
  useQueueReasonsLive(open)

  const total = q.data?.total ?? 0
  const queues = q.data?.queues ?? []
  // A selection is only as real as the rows it points at: ids the server no longer
  // lists (delivered on their own, expired, discarded elsewhere) drop out of it.
  const live = new Set(queues.flatMap((qu) => qu.queued.map((m) => m.id)))
  const picked = [...selected].filter((id) => live.has(id))

  /*
   * Every action invalidates the roster as well as the queues.
   *
   * A release that lands increments the recipient's unread count, and that count is
   * drawn from `idle-status` — so refreshing only this panel empties the list while the
   * rail still shows nothing new arrived. Two views of one event disagreeing is the
   * bug that gets reported as "the message vanished".
   */
  const after = () => {
    void qc.invalidateQueries({ queryKey: ['agent-queues'] })
    void qc.invalidateQueries({ queryKey: ['idle-status'] })
  }
  const release = useMutation({ mutationFn: (s?: string) => releaseAgentQueue(s), onSuccess: after })
  const clear = useMutation({ mutationFn: (s?: string) => clearAgentQueue(s), onSuccess: after })
  const deliverSome = useMutation({
    mutationFn: (ids: string[]) => releaseQueuedMessages(ids),
    onSuccess: (r, ids) => {
      // Ticks stay only on what stayed: a delivered row is gone, a held one is
      // still there and still chosen.
      const stayed = new Set(r.held.map((h) => h.id))
      setSelected((prev) => new Set([...prev].filter((id) => !ids.includes(id) || stayed.has(id))))
      after()
    },
  })
  const discardSome = useMutation({
    mutationFn: (ids: string[]) => discardQueuedMessages(ids),
    onSuccess: (_r, ids) => {
      setSelected((prev) => new Set([...prev].filter((id) => !ids.includes(id))))
      after()
    },
  })
  /*
   * A REFUSED ACTION MUST NOT LOOK LIKE A DONE ONE. Neither of these surfaced anything
   * on failure: the button un-disabled, the count stayed where it was, and the panel
   * said nothing — which reads as "delivered, and the recipient had nothing new" rather
   * than "that did not happen".
   *
   * `Discard all` is the sharper case, because it is irreversible if it DOES land: a
   * silent failure and a silent success are the same picture, so a person cannot tell
   * whether to press it again.
   *
   * Rendered from the mutation's own `error` rather than through an onError callback,
   * matching FileBrowser's `mutErr`, ProjectFiles' `del.error` and Sidebar's
   * `kill.error` — the shape this panel was the last one missing.
   */
  const rawErr = release.error ?? clear.error ?? deliverSome.error ?? discardSome.error
  // A machine whose edge predates the per-message routes answers 404. That is not a
  // failure of the message; it is the machine saying it needs updating, so say that.
  const actErr =
    rawErr instanceof ApiError && rawErr.status === 404 && /_ids$/.test(rawErr.path)
      ? 'This machine’s edge is too old for per-message actions — update it (Machines → Update now).'
      : rawErr
  /*
   * And a chosen message that did NOT go is said in words, not implied by a row that
   * is still there: the edge answers per id, and the first reason is the one shown.
   */
  const heldNote =
    deliverSome.data && deliverSome.data.remaining > 0
      ? `${deliverSome.data.remaining} of ${deliverSome.data.delivered + deliverSome.data.remaining} stayed: ${deliverSome.data.detail ?? 'still held'}`
      : null
  const busy = release.isPending || clear.isPending || deliverSome.isPending || discardSome.isPending

  const onSelect = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })

  // Nothing held, nothing to show. `isPending` counts as nothing: a button that
  // flickers in on every poll while the count loads would be worse than one that
  // arrives a moment late.
  if (total === 0 && !open) return null

  return (
    <>
      <button
        type="button"
        className="qbell"
        aria-label={total > 0 ? `${total} message${total === 1 ? '' : 's'} waiting` : 'Message queue'}
        title={total > 0 ? `${total} waiting to be delivered` : 'Nothing waiting'}
        onClick={() => {
          setOpen(true)
          // Opening is asking: the sheet shows the edge's answer now, not the count
          // this button last heard — a lost queue_changed ends here at the latest.
          void q.refetch()
        }}
      >
        {/* Not a bell. The chip a few pixels away already draws `notifications`
            decoratively, and a live badged bell beside a dead one is the "two doors,
            one of them invisible" this file's neighbours have been bitten by. `pause`
            is also the truer word: these messages are not unread, they are stopped. */}
        <Icon name="pause" size={15} />
        {total > 0 && <span className="qbell-n">{total > 99 ? '99+' : total}</span>}
      </button>

      {open && (
        <Sheet title="Message queue" onClose={() => setOpen(false)}>
          {q.isError ? (
            // An unreachable endpoint and an empty queue look identical and mean
            // opposite things.
            <div className="act-empty act-err">Couldn’t read the queues.</div>
          ) : total === 0 ? (
            <div className="act-empty">
              Nothing waiting.
              <div className="qs-hint">
                A message is held while its recipient is at a prompt, mid-turn, or its user
                is typing or speaking, and delivered on its own once they are free.
              </div>
            </div>
          ) : (
            <>
              <div className="qbell-bar">
                {picked.length > 0 ? (
                  <>
                    <button
                      type="button"
                      className="qbell-act"
                      disabled={busy}
                      onClick={() => deliverSome.mutate(picked)}
                    >
                      {deliverSome.isPending ? 'Delivering…' : `Deliver selected (${picked.length})`}
                    </button>
                    <button
                      type="button"
                      className="qbell-act danger"
                      disabled={busy}
                      onClick={() => discardSome.mutate(picked)}
                    >
                      Discard selected ({picked.length})
                    </button>
                    <button
                      type="button"
                      className="qbell-act quiet"
                      disabled={busy}
                      onClick={() => setSelected(new Set())}
                    >
                      Clear selection
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="qbell-act"
                      disabled={busy}
                      onClick={() => release.mutate(undefined)}
                    >
                      {release.isPending ? 'Delivering…' : 'Deliver now'}
                    </button>
                    <button
                      type="button"
                      className="qbell-act danger"
                      disabled={busy}
                      onClick={() => clear.mutate(undefined)}
                    >
                      Discard all
                    </button>
                  </>
                )}
              </div>
              {actErr && <div className="act-empty act-err">{String(actErr)}</div>}
              {heldNote && !actErr && <div className="qs-hint qbell-note">{heldNote}</div>}
              {/*
               * Said before the buttons are used, not after they disappoint. "Deliver
               * now" cannot force a message into a pane whose user is mid-keystroke —
               * that is the keypress-stealing this queue was built to end — so a
               * recipient who is still busy keeps its mail and the count does not move.
               * Without this line that reads as the button being broken.
               */}
              <div className="qs-hint qbell-note">
                Delivering asks each recipient; one that is still busy keeps its messages.
                Tick messages to act on just those, or use the buttons on a row or a sender.
              </div>
              <div className="qs-list">
                {queues.map((qu) => (
                  <div className="qbell-row" key={`${qu.to_host ?? ''}/${qu.to_session}`}>
                    <QueueGroup
                      qu={qu}
                      actions={{
                        selected,
                        onSelect,
                        onDeliver: (ids) => deliverSome.mutate(ids),
                        onDiscard: (ids) => discardSome.mutate(ids),
                        busy,
                      }}
                    />
                    <div className="qbell-rowacts">
                      <button
                        type="button"
                        className="qbell-act"
                        disabled={busy}
                        onClick={() => release.mutate(qu.to_session)}
                      >
                        Deliver
                      </button>
                      <button
                        type="button"
                        className="qbell-act danger"
                        disabled={busy}
                        onClick={() => clear.mutate(qu.to_session)}
                      >
                        Discard
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Sheet>
      )}
    </>
  )
}
